import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * The plant finder (blocks/plant-finder.liquid, B-62).
 *
 * Every in-stock plant is already on the page, hidden, with its traits in data attributes. Five
 * one-tap questions (light, pets, care, place, look); this scores each plant against the answers,
 * shows the best eight with the taglines of the traits that matched, plus the best-matching set, and
 * keeps the answers in the URL so a result can be shared or reloaded.
 *
 * Pet safety is the only hard filter; a sun-loving plant is never offered for a dark corner and a
 * thirsty one never to someone who forgets to water. Everything else only moves the score, so
 * the page never ends empty while plants are in stock. "Care" feeds both watering and care level.
 */

const RESULT_COUNT = 8;
const MAX_REASONS = 3;

/** @typedef {{ score: number, excluded: boolean, mismatch: boolean, reasons: { key: string, weight: number }[] }} Score */

class PlantFinder extends HTMLElement {
  /** @type {Record<string, string>} */
  answers = {};
  step = 0;

  connectedCallback() {
    this.questions = /** @type {HTMLFieldSetElement[]} */ ([...this.querySelectorAll('[data-question]')]);
    this.quiz = /** @type {HTMLElement} */ (this.querySelector('[data-quiz]'));
    this.results = /** @type {HTMLElement} */ (this.querySelector('[data-results]'));
    this.intro = /** @type {HTMLElement} */ (this.querySelector('.plant-finder__intro'));
    this.strings = this.#readStrings();

    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#restoreFromUrl);
    this.#restoreFromUrl();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#restoreFromUrl);
  }

  #readStrings() {
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-strings]'));
    /** @type {Record<string, string>} */
    const strings = {};
    template?.content.querySelectorAll('[data-key]').forEach((node) => {
      strings[/** @type {HTMLElement} */ (node).dataset.key || ''] = node.textContent?.trim() || '';
    });
    return strings;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const answer = /** @type {HTMLButtonElement | null} */ (target.closest('.plant-finder__answer'));
    if (answer) {
      this.answers[this.#currentName()] = answer.dataset.value || '';
      this.#advance();
      return;
    }

    const action = /** @type {HTMLElement | null} */ (target.closest('[data-action]'));
    if (!action) return;

    switch (action.dataset.action) {
      case 'start':
        this.answers = {};
        this.#showStep(0);
        break;
      case 'back':
        this.#showStep(this.step - 1);
        break;
      case 'skip':
        delete this.answers[this.#currentName()];
        this.#advance();
        break;
      case 'change':
        this.#showStep(0);
        break;
      case 'restart':
        this.answers = {};
        this.#writeUrl(false);
        this.#showStep(0);
        break;
      case 'add':
        this.#addToCart(/** @type {HTMLButtonElement} */ (action));
        break;
    }
  };

  #currentName() {
    return this.questions[this.step]?.dataset.question || '';
  }

  #advance() {
    if (this.step + 1 >= this.questions.length) this.#showResults(true);
    else this.#showStep(this.step + 1);
  }

  /** @param {number} index */
  #showStep(index) {
    this.step = Math.max(0, Math.min(index, this.questions.length - 1));
    this.intro.hidden = true;
    this.results.hidden = true;
    this.quiz.hidden = false;
    this.questions.forEach((fieldset, i) => {
      fieldset.hidden = i !== this.step;
      // Going back shows the answer given before.
      const chosen = this.answers[fieldset.dataset.question || ''];
      fieldset.querySelectorAll('.plant-finder__answer').forEach((node) => {
        const button = /** @type {HTMLElement} */ (node);
        button.toggleAttribute('data-selected', button.dataset.value === chosen);
      });
    });

    const text = this.querySelector('[data-progress-text]');
    if (text) {
      text.textContent = (this.strings.progress || '')
        .replace('[current]', String(this.step + 1))
        .replace('[total]', String(this.questions.length));
    }
    this.querySelectorAll('[data-step]').forEach((dot, i) => {
      dot.toggleAttribute('data-done', i < this.step);
      dot.toggleAttribute('data-current', i === this.step);
    });

    this.#focus(this.questions[this.step]?.querySelector('legend'));
  }

  /** @param {Element | null | undefined} node */
  #focus(node) {
    if (!(node instanceof HTMLElement)) return;
    node.focus({ preventScroll: true });
    const top = this.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight / 2) {
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  /** @param {boolean} pushHistory */
  #writeUrl(pushHistory) {
    const url = new URL(window.location.href);
    this.questions.forEach((fieldset) => url.searchParams.delete(fieldset.dataset.question || ''));
    url.searchParams.delete('result');
    if (pushHistory) {
      for (const [name, value] of Object.entries(this.answers)) url.searchParams.set(name, value);
      url.searchParams.set('result', '1');
      window.history.pushState({}, '', url);
    } else {
      window.history.replaceState({}, '', url);
    }
  }

  #restoreFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    this.answers = {};
    if (params.get('result') !== '1') {
      this.intro.hidden = false;
      this.quiz.hidden = true;
      this.results.hidden = true;
      return;
    }
    this.questions.forEach((fieldset) => {
      const name = fieldset.dataset.question || '';
      const value = params.get(name);
      if (value && fieldset.querySelector(`.plant-finder__answer[data-value="${CSS.escape(value)}"]`)) {
        this.answers[name] = value;
      }
    });
    this.#showResults(false);
  };

  /**
   * Scores one plant against the answers.
   * @param {HTMLElement} card
   * @returns {Score}
   */
  #score(card) {
    const data = card.dataset;
    const num = (/** @type {string | undefined} */ value) => Number(value) || 0;
    const { light, pets, care, place, look } = this.answers;
    let score = 0;
    let excluded = false;
    let mismatch = false;
    /** @type {{ key: string, weight: number }[]} */
    const reasons = [];
    const reason = (/** @type {string} */ key, /** @type {number} */ weight) => reasons.push({ key, weight });

    // Pets: only plants confirmed safe ("unknown" is not safe).
    if (pets === 'yes') {
      if (data.petSafety !== 'safe') excluded = true;
      else reason('pet_safety', 10);
    }

    // Light: what the spot offers against what the plant needs.
    const plantLight = data.light;
    if (light === 'low') {
      if (plantLight === 'low') (score += 3), reason('light', 6);
      else if (plantLight === 'direct_sun') excluded = true;
      else (score -= 3), (mismatch = true);
    } else if (light === 'bright') {
      if (plantLight === 'bright_indirect') (score += 3), reason('light', 6);
      else if (plantLight === 'low') score += 2;
      else score -= 1;
    } else if (light === 'sun') {
      if (plantLight === 'direct_sun') (score += 3), reason('light', 6);
      else if (plantLight === 'bright_indirect') score += 1;
      else (score -= 2), (mismatch = true);
    }

    // Care: watering (1 low … 3 high) and care level (1 unkillable … 4 expert) together.
    const thirst = num(data.water);
    const difficulty = num(data.difficulty);
    if (care === 'low') {
      if (thirst === 1) (score += 2), reason('water', 5);
      else if (thirst === 2) score -= 1.5;
      else if (thirst === 3) excluded = true;
      if (difficulty === 1) (score += 3), reason('difficulty', 6);
      else if (difficulty === 2) score += 1;
      else if (difficulty >= 3) (score -= 2.5 * (difficulty - 2)), (mismatch = true);
    } else if (care === 'moderate') {
      if (thirst === 2) (score += 2), reason('water', 3);
      else if (thirst === 1) score += 1;
      else if (thirst === 3) score -= 1.5;
      if (difficulty && difficulty <= 2) (score += 2), reason('difficulty', 4);
      else if (difficulty === 4) (score -= 2.5), (mismatch = true);
    } else if (care === 'high') {
      if (thirst === 3) score += 1;
      if (difficulty >= 3) (score += difficulty - 1), reason('difficulty', 4);
    }

    // Place: shape (habit) and mature size (1 tiny … 5 extra large).
    const shapes = (data.habit || '').split(' ');
    const size = num(data.size);
    if (place === 'desk') {
      if (shapes.includes('compact')) (score += 2), reason('habit:compact', 4);
      if (size && size <= 2) (score += 2), reason('size', 4);
      else if (size >= 4) score -= 1.5;
    } else if (place === 'shelf') {
      if (shapes.includes('trailing')) (score += 3), reason('habit:trailing', 5);
      else if (size && size <= 3) score += 1;
      if (size === 5) score -= 1;
    } else if (place === 'pole') {
      if (shapes.includes('climbing')) (score += 3), reason('habit:climbing', 5);
    }

    // Look: plain green, patterned (variegation 1 … 5) or rare (rarity 1 … 4).
    const variegation = num(data.variegation);
    const rarity = num(data.rarity);
    if (look === 'green' && variegation && variegation <= 2) (score += 2), reason('variegation', 3);
    if (look === 'pattern' && variegation >= 3) (score += 1 + 0.5 * (variegation - 2)), reason('variegation', 3);
    if (look === 'rare' && rarity >= 3) (score += 1 + 0.5 * (rarity - 2)), reason('rarity', 3);

    reasons.sort((a, b) => b.weight - a.weight);
    return { score, excluded, mismatch, reasons };
  }

  /** @param {boolean} pushHistory */
  #showResults(pushHistory) {
    if (pushHistory) this.#writeUrl(true);
    this.intro.hidden = true;
    this.quiz.hidden = true;
    this.results.hidden = false;

    const list = /** @type {HTMLElement} */ (this.querySelector('[data-list]'));
    const cards = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('[data-plant]')]);
    /** @type {Map<string, Score>} */
    const scores = new Map();
    for (const card of cards) scores.set(card.dataset.handle || '', this.#score(card));
    const scoreOf = (/** @type {HTMLElement} */ card) => scores.get(card.dataset.handle || '');

    const shown = cards
      .filter((card) => !scoreOf(card)?.excluded)
      .sort((a, b) => (scoreOf(b)?.score || 0) - (scoreOf(a)?.score || 0) || Number(b.dataset.stock) - Number(a.dataset.stock))
      .slice(0, RESULT_COUNT);

    cards.forEach((card) => (card.hidden = true));
    shown.forEach((card, index) => {
      list.append(card);
      card.hidden = false;
      const best = /** @type {HTMLElement | null} */ (card.querySelector('[data-best]'));
      if (best) best.hidden = index !== 0;
      card.toggleAttribute('data-top', index === 0);
      this.#showReasons(card, scoreOf(card)?.reasons || []);
    });

    const none = /** @type {HTMLElement} */ (this.querySelector('[data-none]'));
    const closest = /** @type {HTMLElement} */ (this.querySelector('[data-closest]'));
    const intro = /** @type {HTMLElement} */ (this.querySelector('[data-results-intro]'));
    none.hidden = shown.length > 0;
    intro.hidden = shown.length === 0;
    closest.hidden = !shown[0] || !scoreOf(shown[0])?.mismatch;

    this.#showSummary();
    this.#showSet(scores);

    const nudge = /** @type {HTMLElement | null} */ (this.querySelector('[data-nudge]'));
    if (nudge) nudge.hidden = !shown.length;

    this.#focus(this.results.querySelector('h2'));
  }

  /** Lists the answers given, as chips taken from the answer buttons themselves. */
  #showSummary() {
    const wrap = /** @type {HTMLElement | null} */ (this.querySelector('[data-summary-wrap]'));
    const list = this.querySelector('[data-summary-list]');
    if (!wrap || !list) return;
    list.replaceChildren();
    for (const fieldset of this.questions) {
      const value = this.answers[fieldset.dataset.question || ''];
      if (!value) continue;
      const button = /** @type {HTMLElement | null} */ (
        fieldset.querySelector(`.plant-finder__answer[data-value="${CSS.escape(value)}"]`)
      );
      if (!button) continue;
      const item = document.createElement('li');
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = button.querySelector('.plant-finder__answer-icon')?.innerHTML || '';
      // A bare "Yes" or "No" needs its own wording out of context (pets).
      const label = button.dataset.summary || button.querySelector('.plant-finder__answer-label')?.textContent?.trim();
      item.append(icon, label || '');
      list.append(item);
    }
    wrap.hidden = !list.children.length;
  }

  /**
   * @param {HTMLElement} card
   * @param {{ key: string }[]} reasons
   */
  #showReasons(card, reasons) {
    const items = /** @type {HTMLElement[]} */ ([...card.querySelectorAll('[data-reason]')]);
    items.forEach((item) => (item.hidden = true));
    const reasonList = /** @type {HTMLElement | null} */ (card.querySelector('.plant-finder__reasons'));
    let shown = 0;
    for (const { key } of reasons) {
      if (shown >= MAX_REASONS) break;
      const [axis, value] = key.split(':');
      const item = items.find(
        (node) => node.dataset.reason === axis && (!value || node.dataset.reasonValue === value)
      );
      if (!item || !item.hidden) continue;
      item.hidden = false;
      reasonList?.append(item);
      shown += 1;
    }
  }

  /**
   * Shows the set whose five plants suit the answers best, if all five pass the filters.
   * @param {Map<string, Score>} scores
   */
  #showSet(scores) {
    const slot = /** @type {HTMLElement | null} */ (this.querySelector('[data-set-slot]'));
    if (!slot) return;
    const sets = /** @type {HTMLElement[]} */ ([...slot.querySelectorAll('[data-set]')]);
    sets.forEach((node) => (node.hidden = true));

    /** @type {HTMLElement | null} */
    let best = null;
    let bestScore = -Infinity;
    for (const node of sets) {
      const results = (node.dataset.plants || '').split(' ').filter(Boolean).map((handle) => scores.get(handle));
      if (!results.length || results.some((result) => !result || result.excluded)) continue;
      const mean = results.reduce((sum, result) => sum + (result?.score || 0), 0) / results.length;
      if (mean > bestScore) (bestScore = mean), (best = node);
    }
    if (best) best.hidden = false;
    slot.hidden = !best;
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.strings.adding || label;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((node) => /** @type {HTMLElement} */ (node).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const added = await response.json();
      if (added.status) throw new Error(added.description || added.message || 'Add to cart failed');

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'plant-finder',
          itemCount: 1,
          sections: added.sections,
          didError: false,
        },
      });
      button.textContent = this.strings.added || label;
      button.dataset.state = 'added';
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
        delete button.dataset.state;
      }, 2000);
    } catch (error) {
      deferred.reject(error);
      button.textContent = this.strings.error || label;
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 3000);
    }
  }
}

if (!customElements.get('plant-finder')) customElements.define('plant-finder', PlantFinder);
